import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface WorkerFulfillmentStat {
  workerId: string;
  name: string;
  email: string | null;
  readyCount: number;
  fulfilledCount: number;
  readyPercent: number;
  fulfilledPercent: number;
}

export interface DateRange {
  start: string;
  end: string;
}

function mapRpcRow(row: Record<string, unknown>): WorkerFulfillmentStat {
  return {
    workerId: String(row.worker_id ?? ''),
    name: String(row.name ?? row.email ?? 'Unknown'),
    email: row.email != null ? String(row.email) : null,
    readyCount: Number(row.ready_count ?? 0),
    fulfilledCount: Number(row.fulfilled_count ?? 0),
    readyPercent: Number(row.ready_percent ?? 0),
    fulfilledPercent: Number(row.fulfilled_percent ?? 0),
  };
}

export function useWorkerFulfillmentStats(
  adminId: string | null,
  dateRange: DateRange | null
): { stats: WorkerFulfillmentStat[]; loading: boolean; error: string | null } {
  const [stats, setStats] = useState<WorkerFulfillmentStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!adminId || !dateRange?.start || !dateRange?.end) {
      setStats([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const { data, error: rpcError } = await supabase.rpc('get_worker_fulfillment_stats', {
          p_admin_id: adminId,
          p_date_start: dateRange.start,
          p_date_end: dateRange.end,
        });

        if (cancelled) return;
        if (rpcError) {
          setError(rpcError.message);
          setStats([]);
          setLoading(false);
          return;
        }
        if (data == null) {
          setStats([]);
          setLoading(false);
          return;
        }
        const arr = Array.isArray(data) ? data : (data as unknown as unknown[]);
        const mapped = arr.map((row) => mapRpcRow(row as Record<string, unknown>));
        setStats(mapped);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load worker stats');
          setStats([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adminId, dateRange?.start, dateRange?.end]);

  return { stats, loading, error };
}
